// @flow
import { flowRight, identity, noop } from 'lodash/fp';

import {
    addTextControlContactsApi,
    deleteTextControlContactsApi,
    getTextControlContactsApi,
    updateTextControlContactsApi
} from '~/core/api/text-control/text-control.api';
import { alerts, feedback } from '~/core/constants';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { GET_CONTACTS, SAVE_CONTACTS } from './constants';
import { receivedContacts, receivedServerErrors, savedContacts } from './actions';
import { getConfig } from '~/core/state/config/selectors';
import { getTextControlContacts } from './selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import messages from './intl';
import { requestAlert } from '~/core/state/alerts/actions';
import type { SaveContactsAction } from './types';

export function* getContactsSaga(): Generator<noop | Effect, *, *> {
    const [contacts, error] = yield call(handleApi(getTextControlContactsApi));

    if (error) {
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }

    yield put(receivedContacts(error || contacts));
}

function* watchGetContactsSaga(): Generator<Effect, *, *> {
    yield takeLatest(GET_CONTACTS, handleSaga(getContactsSaga));
}

const makeContact = numberPrefix => ({ id, name, mobile }) => {
    return {
        id,
        name,
        mobile:
            numberPrefix && mobile.startsWith(numberPrefix.get('local'))
                ? mobile.replace(numberPrefix.get('local'), numberPrefix.get('international'))
                : mobile
    };
};

export function* saveContactsSaga({
    payload: { contacts, formId }
}: SaveContactsAction): Generator<Effect | noop, *, *> {
    const currentContacts = yield select(getTextControlContacts);
    const nextIds = contacts.map(c => c.id).filter(identity);
    const numberPrefix = yield select(getConfig, 'textControl:numberPrefix');
    const deletedContactIds = currentContacts
        .toSeq()
        .map(c => c.get('id'))
        .filter(id => !nextIds.includes(id))
        .filter(identity)
        .toJS();

    const apiContacts = contacts.map(makeContact(numberPrefix));

    const newContacts = apiContacts.filter(c => !c.id && c.name && c.mobile);
    const updatedContacts = apiContacts.filter(c => c.id && c.name && c.mobile);

    let addedContacts;
    let addContactsError;
    let deleteContactsError;

    if (newContacts.length) {
        [addedContacts, addContactsError] = yield call(handleApi(addTextControlContactsApi), ...newContacts);
    }
    if (deletedContactIds.length) {
        [, deleteContactsError] = yield call(handleApi(deleteTextControlContactsApi), ...deletedContactIds);
    }

    const [updated, updateContactsError] = yield call(handleApi(updateTextControlContactsApi), ...updatedContacts);

    const exception = addContactsError || deleteContactsError || updateContactsError;

    if (exception) {
        const alert = flowRight(put, requestAlert);
        yield alert(messages.errorSavingContacts, alerts.types.ERROR);
        yield put(savedContacts(exception));
    }

    const errors = [...(updated || []), ...(addedContacts || [])].map(x => x.error).filter(identity);

    const hasErrors = !!errors.length;

    yield put(receivedServerErrors(hasErrors ? errors : []));
    yield put(savedContacts({ updatedContacts, addedContacts }));
}

function* watchSaveContactsSaga(): Generator<Effect, *, *> {
    yield takeLatest(SAVE_CONTACTS, handleSaga(saveContactsSaga));
}

export default [watchGetContactsSaga, watchSaveContactsSaga];



// WEBPACK FOOTER //
// ./src/core/state/text-control/sagas.js